/**
 * Driver-self-service — Compliance Status Panel (Plan §2.2 M-F-21).
 * Phase 4 G5. Consumes GET /driver/me/compliance (DRIVER_OWN_COMPLIANCE_STATUS).
 *
 * Mounted inside DriverDashboard via M-F-20. Driver Dashboard remains the
 * SINGLE AUTHORITATIVE self-service surface.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { ShieldCheck, ShieldAlert } from 'lucide-react';

const API_URL = (import.meta.env.VITE_BACKEND_URL || '') + '/api';

const DriverComplianceStatusPanel = ({ token, theme }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/driver/me/compliance`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
      setError(null);
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const eligible = data && data.dispatch_eligibility && data.dispatch_eligibility.is_eligible;
  const reasons  = (data && data.dispatch_eligibility && data.dispatch_eligibility.reasons) || [];

  return (
    <div className={`${theme.bgCard} ${theme.border} border rounded-2xl p-4`} data-testid="driver-self-compliance-status">
      <div className="flex items-center justify-between mb-3">
        <h3 className={`${theme.text} text-base font-semibold flex items-center gap-2`}>
          {eligible ? (
            <ShieldCheck className="h-5 w-5 text-emerald-400" />
          ) : (
            <ShieldAlert className="h-5 w-5 text-amber-400" />
          )}
          Ma conformité
        </h3>
        <button
          type="button"
          onClick={load}
          className={`${theme.textSub} text-xs hover:underline`}
        >
          ↻
        </button>
      </div>
      {loading && <p className={`${theme.textSub} text-sm`}>Chargement…</p>}
      {error && <p className="text-rose-400 text-sm">Erreur</p>}
      {!loading && !error && data && (
        <>
          <div className={`${theme.textSub} text-sm mb-2`}>
            État : <span className={theme.text}>{data.lifecycle_state || '—'}</span>
          </div>
          <div className={`${theme.textSub} text-sm mb-2`}>
            Statut de conformité : <span className={theme.text}>{data.compliance_state || '—'}</span>
          </div>
          {eligible ? (
            <div className="text-emerald-400 text-sm">✓ Éligible aux courses</div>
          ) : (
            <div className="text-amber-400 text-sm">
              Inéligible
              {reasons.length > 0 && (
                <ul className={`${theme.textSub} list-disc list-inside mt-1 text-xs`}>
                  {reasons.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DriverComplianceStatusPanel;
